import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardLinkButton,
  CardMeta,
  CardText,
  type CardAction,
} from '@/components/agent-chat/card-parts.component';
import {
  usePlanGateRoute,
  useStartCardTrial,
} from '@/components/upgrade/use-plan-gate.hook';
import { ComparePlansDialog } from '@/containers/onboarding/compare-plans-dialog.component';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOpenUpgrade } from '@/hooks/use-open-upgrade.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import {
  appSumoGatePriceLine,
  appSumoUpgradeSearch,
  BOOK_A_CALL,
  safeCallUrl,
  SEE_YOUR_PRICE,
  trackAppSumoUpgrade,
  withCleanlistPlanName,
} from '@/lib/appsumo-upgrade';
import { formatRenewalPrice } from '@/lib/card-trial';
import { iconLock } from '@/lib/icons';
import { toUpgradeTier } from '@/lib/upgrade-link';

/**
 * A step the workspace's plan leaves out, under Clu's line saying so, as a
 * card: the plan, the API's sentence, and the way in every gated page offers
 * (PlanGate): the Pro trial when the workspace can still start it, an
 * AppSumo plan's upgrade offer while it is open, the upgrade otherwise. The
 * API names the plan (`required_plan`, cleanse-api
 * copilot/tool_permissions.py); a reply from before that carries
 * `plan_name`.
 */
export function PlanRequiredCard({
  result,
}: {
  result: Record<string, unknown>;
}) {
  const named = result.required_plan ?? result.plan_name;
  const plan = typeof named === 'string' && named.trim() ? named : 'Starter';
  const sentence =
    typeof result.message === 'string' && result.message.trim()
      ? result.message
      : `This needs the ${plan} plan.`;
  const { route, v4 } = usePlanGateRoute({ plan });
  if (route === 'trial')
    return <TrialPlanCard plan={plan} sentence={sentence} v4={v4} />;
  if (route === 'appsumo')
    return <AppSumoPlanCard plan={plan} sentence={sentence} />;
  return <UpgradePlanCard plan={plan} sentence={sentence} v4={v4} />;
}

/** The card itself: the plan in the header, the sentence, a line, the footer. */
function PlanCard({
  title,
  sentence,
  meta,
  footer,
}: {
  title: string;
  sentence: string;
  meta?: string;
  footer?: ReactNode;
}) {
  return (
    <AgentCard icon={iconLock} title={title} data-plan-required>
      <div className='flex flex-col gap-[4px]'>
        <CardText>{sentence}</CardText>
        {meta ? <CardMeta>{meta}</CardMeta> : null}
      </div>
      {footer}
    </AgentCard>
  );
}

/** "See plans": every plan side by side on the v4 catalog, the plans page
 *  otherwise. */
function SeePlansLink({
  v4,
  plan,
  trial,
}: {
  v4: boolean;
  plan: string;
  trial?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const openUpgrade = useOpenUpgrade();
  return (
    <>
      <CardLinkButton
        onClick={() =>
          v4 ? setOpen(true) : openUpgrade({ source: 'upgrade_card' })
        }
      >
        See plans
      </CardLinkButton>
      {v4 ? (
        <ComparePlansDialog
          plan={plan}
          trial={trial}
          open={open}
          onOpenChange={setOpen}
        />
      ) : null}
    </>
  );
}

/**
 * Opens the upgrade on the plan: the wizard on its plan, or the plans page.
 * A navigation has nothing to wait for, so the card's button frees at once
 * (false) rather than spin on a chat that stays open.
 */
function upgrade(openUpgrade: ReturnType<typeof useOpenUpgrade>, plan: string) {
  openUpgrade({ plan: toUpgradeTier(plan), source: 'upgrade_card' });
  return false;
}

/** Upgrade to the plan, every plan a link away. */
function UpgradePlanCard({
  plan,
  sentence,
  v4,
}: {
  plan: string;
  sentence: string;
  v4: boolean;
}) {
  const openUpgrade = useOpenUpgrade();
  return (
    <PlanCard
      title={`Part of ${plan}`}
      sentence={sentence}
      footer={
        <CardFooter
          links={<SeePlansLink v4={v4} plan={plan} />}
          primary={{
            label: `Upgrade to ${plan}`,
            onClick: () => upgrade(openUpgrade, plan),
          }}
        />
      }
    />
  );
}

/**
 * An AppSumo plan alone names the plan "Cleanlist Pro" (AppSumo's own tiers
 * are called Starter and Pro too). While its upgrade price is open the way
 * in is that offer, with its price and a call; otherwise the plans.
 */
function AppSumoPlanCard({
  plan,
  sentence,
}: {
  plan: string;
  sentence: string;
}) {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const openUpgrade = useOpenUpgrade();
  const { offer, prices, phase, active, isLoading } = useAppSumoUpgrade();
  const offerOpen = active && offer && prices ? offer : null;

  // Once per card shown with the offer, not on every refetch of it.
  const viewed = useRef(false);
  useEffect(() => {
    if (!offerOpen || viewed.current) return;
    viewed.current = true;
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_gate_viewed', {
      feature: 'generic',
      tier: offerOpen.appsumo_tier,
      phase,
    });
  }, [offerOpen, phase, posthog]);

  const title = withCleanlistPlanName(`Part of ${plan}`);
  // It waits for the offer rather than show the plans and swap them.
  if (isLoading) return <PlanCard title={title} sentence={sentence} />;
  if (offerOpen && prices) {
    const callUrl = safeCallUrl(offerOpen.call_url);
    const tier = offerOpen.appsumo_tier;
    return (
      <PlanCard
        title={title}
        sentence={sentence}
        meta={appSumoGatePriceLine(prices, offerOpen.ends_at)}
        footer={
          <CardFooter
            links={
              callUrl ? (
                <a
                  href={callUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() =>
                    trackAppSumoUpgrade(
                      posthog,
                      'appsumo_upgrade_call_clicked',
                      { source: 'gate', tier }
                    )
                  }
                  className={CARD_LINK}
                >
                  {BOOK_A_CALL}
                </a>
              ) : undefined
            }
            primary={{
              label: SEE_YOUR_PRICE,
              onClick: () => {
                trackAppSumoUpgrade(posthog, 'appsumo_upgrade_gate_clicked', {
                  feature: 'generic',
                  tier,
                });
                void navigate({
                  to: '/upgrade',
                  search: appSumoUpgradeSearch({
                    source: 'gate',
                    plan: 'pro',
                  }),
                });
                return false;
              },
            }}
          />
        }
      />
    );
  }
  return (
    <PlanCard
      title={title}
      sentence={sentence}
      footer={
        <CardFooter
          primary={{
            label: 'See plans',
            onClick: () => upgrade(openUpgrade, plan),
          }}
        />
      }
    />
  );
}

/**
 * The Pro trial as the way in, Checkout started from the card. The footer
 * is the workspace's own, so switching workspaces mid-Checkout never
 * inherits a locked button, and Back from Checkout frees it again.
 */
function TrialPlanCard({
  plan,
  sentence,
  v4,
}: {
  plan: string;
  sentence: string;
  v4: boolean;
}) {
  const { trial, organizationId } = useFeatureAccess();
  const startTrial = useStartCardTrial();
  const [restored, setRestored] = useState(0);
  usePageRestored(() => setRestored((count) => count + 1));
  const renewal = trial?.renews_to;

  const primary: CardAction = {
    label: 'Start free 14-day trial',
    workingLabel: 'Opening checkout',
    // False, Checkout not opened, unlocks the button.
    onClick: () => startTrial(),
  };

  return (
    <PlanCard
      title={`Part of ${plan}`}
      sentence={sentence}
      meta={
        renewal
          ? `${formatRenewalPrice(renewal)} after your trial ends. Cancel anytime before and you won't be charged.`
          : undefined
      }
      footer={
        <CardFooter
          key={`${organizationId ?? ''}:${restored}`}
          links={<SeePlansLink v4={v4} plan='Pro' trial />}
          primary={primary}
        />
      }
    />
  );
}
